import { useRef, useState } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  ArrowLeft,
  BadgeCheck,
  Download,
  Search,
  ShieldX,
  Sparkles,
  Loader2,
} from "lucide-react";
import MembershipCardPreview from "@/components/MembershipCardPreview";
import Footer from "@/components/Footer";
import { maskMobile } from "@/lib/membership";
import { api, fmtDate } from "@/lib/api";
import { downloadCardImage, cardFileName } from "@/lib/downloadCard";

export default function Verify() {
  const [num, setNum] = useState("");
  const [result, setResult] = useState(null);
  const [searched, setSearched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errMsg, setErrMsg] = useState("");
  const [downloading, setDownloading] = useState(false);
  const cardRef = useRef(null);

  const downloadCard = async () => {
    if (!cardRef.current || !result) return;
    setDownloading(true);
    try {
      await downloadCardImage(
        cardRef.current,
        cardFileName(result.membership_number, result.patient_name)
      );
      toast.success("Card downloaded");
    } catch {
      toast.error("Could not download card");
    } finally {
      setDownloading(false);
    }
  };

  const onSearch = async (e) => {
    e.preventDefault();
    setLoading(true);
    setSearched(true);
    setResult(null);
    setErrMsg("");
    try {
      const { data } = await api.get("/patients/verify", {
        params: { code: num.trim().toUpperCase() },
      });
      if (!data.found) {
        setErrMsg("No membership found");
      } else {
        const m = data.membership;
        setResult({
          membership_number: m.code,
          patient_name:      m.patientName,
          patient_mobile:    m.phone,
          issued_at:         m.validFrom,
          expires_at:        m.validTo,
          agent_name:        m.agentName || null,
          valid:             data.valid,
        });
      }
    } catch (err) {
      setErrMsg(err?.response?.data?.detail || "No membership found");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="relative min-h-screen bg-gradient-soft">
      <Navbar />
      <div className="absolute inset-0 grid-pattern opacity-40 pointer-events-none" />
      <div className="relative max-w-3xl mx-auto px-5 sm:px-8 pt-28 pb-20">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-6"
        >
          <ArrowLeft className="h-4 w-4" /> Back to home
        </Link>

        <Card className="border-border/70 shadow-soft">
          <CardContent className="p-6 sm:p-8">
            <div className="flex items-center gap-3 mb-2">
              <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-primary text-primary-foreground shadow-elegant">
                <BadgeCheck className="h-5 w-5" />
              </span>
              <h1 className="font-display text-2xl sm:text-3xl font-bold text-foreground tracking-tight">
                Verify a membership
              </h1>
            </div>
            <p className="text-sm text-muted-foreground">
              Clinic staff and members can quickly check the validity of a 1Rupee Clinic card.
            </p>

            <form onSubmit={onSearch} className="mt-6 flex flex-col sm:flex-row gap-3">
              <div className="flex-1 space-y-2">
                <Label htmlFor="num" className="sr-only">
                  Membership number or mobile number
                </Label>
                <Input
                  id="num"
                  placeholder="Enter mobile number or membership number"
                  value={num}
                  onChange={(e) => setNum(e.target.value)}
                  className="h-11 font-mono-card tracking-wider"
                />
              </div>
              <Button
                type="submit"
                size="lg"
                disabled={loading || !num}
                className="h-11 bg-gradient-primary text-primary-foreground rounded-xl shadow-elegant"
              >
                {loading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Search className="h-4 w-4" />
                )}
                Verify
              </Button>
            </form>

            {searched && result && (
              <div className="mt-8 animate-fade-up">
                <div className="flex items-center gap-2 mb-4">
                  <Badge className="bg-success/15 text-success border border-success/30 hover:bg-success/15">
                    <BadgeCheck className="h-3.5 w-3.5 mr-1" /> Valid membership
                  </Badge>
                  <span className="text-xs text-muted-foreground">
                    Until {fmtDate(result.expires_at)}
                  </span>
                </div>
                <MembershipCardPreview
                  name={result.patient_name}
                  mobile={result.patient_mobile}
                  membershipNumber={result.membership_number}
                  issued={fmtDate(result.issued_at)}
                  expires={fmtDate(result.expires_at)}
                  innerRef={cardRef}
                  variant="flat"
                />
                <div className="mt-4 flex justify-center">
                  <Button
                    onClick={downloadCard}
                    disabled={downloading}
                    className="bg-gradient-primary text-primary-foreground rounded-xl shadow-elegant"
                  >
                    {downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                    {downloading ? "Preparing…" : "Download Card"}
                  </Button>
                </div>
                <div className="mt-5 grid sm:grid-cols-2 gap-3 text-sm">
                  <div className="rounded-xl border border-border bg-background p-3">
                    <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                      Cardholder
                    </div>
                    <div className="font-semibold text-foreground truncate">
                      {result.patient_name}
                    </div>
                  </div>
                  <div className="rounded-xl border border-border bg-background p-3">
                    <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                      Mobile
                    </div>
                    <div className="font-semibold text-foreground truncate">
                      {maskMobile(result.patient_mobile)}
                    </div>
                  </div>
                </div>
                {result.agent_name && (
                  <p className="mt-3 text-xs text-muted-foreground">
                    Sold by{" "}
                    <span className="font-medium text-foreground">{result.agent_name}</span>
                  </p>
                )}
              </div>
            )}

            {searched && !loading && !result && (
              <div className="mt-8 rounded-2xl border border-destructive/30 bg-destructive/5 p-5 flex items-start gap-3 animate-fade-up">
                <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-destructive/15 text-destructive">
                  <ShieldX className="h-5 w-5" />
                </span>
                <div>
                  <div className="font-semibold text-foreground">{errMsg || "No membership found"}</div>
                  <div className="text-sm text-muted-foreground mt-0.5">
                    Verify the alphanumeric ID or generate a new membership.
                  </div>
                  <Link to="/generate">
                    <Button
                      size="sm"
                      className="mt-3 bg-gradient-primary text-primary-foreground rounded-lg"
                    >
                      <Sparkles className="h-4 w-4" /> Generate new card
                    </Button>
                  </Link>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
      <Footer />
    </main>
  );
}
